/* Mind Glow Glass — Illuminated House Signs concept renderer (theme copy)
 * Builds an SVG scene (facade + black glass sign + engraving + HALO light)
 * from a configuration object. Pure string output, no dependencies.
 * Concept visualisation only — not a photograph of a finished product.
 */
(function (global) {
  'use strict';

  var PX_PER_MM = 0.95; // facade view scale: 1200 px ≈ 1.26 m of wall

  var LED = {
    warm: { core: '#FFF1D6', glow: '#FFB760', halo: '#FFAE52', label: 'Warm White 3000K' },
    cool: { core: '#F6FAFF', glow: '#BFD7FF', halo: '#C4DAFF', label: 'Cool White 6000K' }
  };

  var SIZES = [[200, 150], [250, 200], [300, 200], [400, 250], [500, 300]];

  var LIGHTING = {
    halo: 'HALO',
    engraving: 'Illuminated Engraving',
    both: 'HALO + Engraving'
  };

  var MODELS = {
    classic: {
      name: 'CLASSIC', tagline: 'Number & street name',
      facade: 'render', uses: ['number', 'street'],
      defaults: { number: '27', street: 'Hawthorn Avenue', house: '' , size: 2, orientation: 'h' },
      description: 'The essential address sign. A confident house number above your street name, reverse-engraved into black Lacobel glass and lit from within.'
    },
    linear: {
      name: 'LINEAR', tagline: 'Number with lines of light',
      facade: 'cladding', uses: ['number', 'street'],
      defaults: { number: '14', street: 'Kingsley Road', house: '', size: 3, orientation: 'h' },
      description: 'Your number framed by fine engraved lines that glow after dark. A quiet, architectural composition for contemporary facades.'
    },
    signature: {
      name: 'SIGNATURE', tagline: 'Number & house name',
      facade: 'porcelain', uses: ['number', 'house', 'street'],
      defaults: { number: '8', street: '', house: 'The Willows', size: 3, orientation: 'h' },
      description: 'For homes with a name. Elegant serif lettering paired with the house number — a warm welcome that glows at the door.'
    },
    modern: {
      name: 'MODERN', tagline: 'Geometric composition',
      facade: 'concrete', uses: ['number', 'street'],
      defaults: { number: '52', street: 'Elm Park Gardens', house: '', size: 3, orientation: 'h' },
      description: 'A geometric layout: the number held within an illuminated ring, balanced by measured lines of light and your street name.'
    },
    custom: {
      name: 'CUSTOM', tagline: 'Individual design',
      facade: 'brick', uses: ['number', 'house', 'street'],
      defaults: { number: '3', street: 'Orchard Lane', house: 'Olive House', size: 4, orientation: 'h' },
      description: 'Your own motif, lettering or artwork, engraved and illuminated to order. The layout is designed with you before production.'
    }
  };

  var FONT_SANS = "Jost, 'Futura', 'Century Gothic', sans-serif";
  var FONT_SERIF = "'Instrument Serif', Georgia, 'Times New Roman', serif";

  /* ---------- helpers ---------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function rng(seed) {
    var a = seed >>> 0;
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function shade(hex, amt) { // amt -1..1
    var n = parseInt(hex.slice(1), 16);
    var r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    function f(c) { return Math.max(0, Math.min(255, Math.round(amt < 0 ? c * (1 + amt) : c + (255 - c) * amt))); }
    return '#' + ((1 << 24) | (f(r) << 16) | (f(g) << 8) | f(b)).toString(16).slice(1);
  }
  function f1(n) { return Math.round(n * 10) / 10; }
  var uidCounter = 0;

  // rough advance widths (em) used for first-pass fitting; DOM pass refines
  function estWidth(text, size, kind, ls) {
    var per = kind === 'num' ? 0.5 : kind === 'serif' ? 0.43 : 0.64;
    return text.length * size * (per + (ls || 0));
  }
  function fit(text, size, maxW, kind, ls) {
    var w = estWidth(text, size, kind, ls);
    return w > maxW ? size * maxW / w : size;
  }
  function wrap2(text, size, maxW, kind, ls) {
    if (estWidth(text, size, kind, ls) <= maxW || text.indexOf(' ') < 0) return [text];
    var words = text.split(' '), best = null;
    for (var i = 1; i < words.length; i++) {
      var a = words.slice(0, i).join(' '), b = words.slice(i).join(' ');
      var m = Math.max(a.length, b.length);
      if (!best || m < best.m) best = { m: m, lines: [a, b] };
    }
    return best.lines;
  }

  /* ---------- sign layouts (local px coords, w × h) ---------- */
  function layout(model, w, h, d) {
    var it = [];
    var vert = h > w;
    var m = Math.min(w, h) * 0.12;
    var sw = Math.max(1.1, Math.min(w, h) * 0.0085);
    var num = (d.number || '').trim();
    var street = (d.street || '').trim().toUpperCase();
    var house = (d.house || '').trim();

    function T(text, x, y, size, opt) {
      opt = opt || {};
      it.push({ t: 'text', text: text, x: x, y: y, size: size, font: opt.font || FONT_SANS,
        weight: opt.weight || 300, style: opt.style || 'normal', ls: opt.ls || 0,
        anchor: opt.anchor || 'middle', maxw: opt.maxw || (w - 2 * m) });
    }
    function L(x1, y1, x2, y2, s) { it.push({ t: 'line', x1: x1, y1: y1, x2: x2, y2: y2, sw: s || sw }); }
    function C(cx, cy, r, s) { it.push({ t: 'circle', cx: cx, cy: cy, r: r, sw: s || sw }); }

    var maxW = w - 2 * m, size, lines, i;

    if (model === 'classic') {
      if (!street) {
        size = fit(num, vert ? w * 0.7 : h * 0.62, maxW, 'num');
        T(num, w / 2, h / 2 + size * 0.36, size);
      } else if (!vert) {
        size = fit(num, h * 0.47, maxW, 'num');
        T(num, w / 2, h * 0.55, size);
        L(w / 2 - w * 0.16, h * 0.655, w / 2 + w * 0.16, h * 0.655);
        var ss = fit(street, h * 0.085, maxW, 'caps', 0.26);
        T(street, w / 2, h * 0.81, ss, { weight: 400, ls: 0.26 });
      } else {
        size = fit(num, Math.min(w * 0.66, h * 0.4), maxW, 'num');
        T(num, w / 2, h * 0.47, size);
        L(w / 2 - w * 0.2, h * 0.565, w / 2 + w * 0.2, h * 0.565);
        var vs = Math.min(w * 0.085, h * 0.055);
        lines = wrap2(street, vs, maxW, 'caps', 0.24);
        vs = fit(lines.reduce(function (a, b) { return a.length > b.length ? a : b; }), vs, maxW, 'caps', 0.24);
        for (i = 0; i < lines.length; i++) T(lines[i], w / 2, h * 0.68 + i * vs * 1.6, vs, { weight: 400, ls: 0.24 });
      }
    }

    else if (model === 'linear') {
      if (!vert) {
        var cy = street ? h * 0.46 : h * 0.5;
        size = fit(num, h * 0.5, w * 0.42, 'num');
        var nw = estWidth(num, size, 'num');
        T(num, w / 2, cy + size * 0.36, size, { maxw: w * 0.42 });
        var gap = size * 0.28, gs = h * 0.05;
        for (i = -1; i <= 1; i++) {
          var y = cy + i * gs, f = i === 0 ? 1 : 0.62;
          var inL = w / 2 - nw / 2 - gap, inR = w / 2 + nw / 2 + gap;
          L(inL - (inL - m) * f, y, inL, y, i === 0 ? sw * 1.15 : sw * 0.8);
          L(inR, y, inR + (w - m - inR) * f, y, i === 0 ? sw * 1.15 : sw * 0.8);
        }
        if (street) T(street, w / 2, h * 0.84, fit(street, h * 0.075, maxW * 0.8, 'caps', 0.3), { weight: 400, ls: 0.3 });
      } else {
        var cyv = street ? h * 0.46 : h * 0.5;
        size = fit(num, w * 0.6, maxW, 'num');
        T(num, w / 2, cyv + size * 0.36, size);
        var gx = w * 0.07, top = cyv - size * 0.62, bot = cyv + size * 0.52;
        var end = street ? h - m * 2.4 : h - m;
        for (i = -1; i <= 1; i++) {
          var x = w / 2 + i * gx, fv = i === 0 ? 1 : 0.6;
          L(x, top - (top - m) * fv, x, top, i === 0 ? sw * 1.15 : sw * 0.8);
          L(x, bot, x, bot + (end - bot) * fv, i === 0 ? sw * 1.15 : sw * 0.8);
        }
        if (street) {
          var lsv = Math.min(w * 0.075, h * 0.045);
          lines = wrap2(street, lsv, maxW, 'caps', 0.26);
          for (i = 0; i < lines.length; i++) T(lines[i], w / 2, h - m * 1.55 + (i - (lines.length - 1)) * lsv * 1.5 + (lines.length - 1) * lsv * 0.75, lsv, { weight: 400, ls: 0.26 });
        }
      }
    }

    else if (model === 'signature') {
      var name = house || 'House Name';
      if (!vert) {
        var ns = h * 0.1;
        if (num) {
          var label = num;
          T(label, w / 2, h * 0.27, ns, { weight: 400, ls: 0.2 });
          var lw = estWidth(label, ns, 'caps', 0.2) / 2 + w * 0.04;
          L(w / 2 - lw - w * 0.12, h * 0.27 - ns * 0.35, w / 2 - lw, h * 0.27 - ns * 0.35, sw * 0.8);
          L(w / 2 + lw, h * 0.27 - ns * 0.35, w / 2 + lw + w * 0.12, h * 0.27 - ns * 0.35, sw * 0.8);
        }
        var hs = fit(name, h * 0.3, maxW, 'serif');
        T(name, w / 2, num ? h * 0.6 : h * 0.58, hs, { font: FONT_SERIF, style: 'italic', weight: 400 });
        if (street) T(street, w / 2, h * 0.83, fit(street, h * 0.065, maxW, 'caps', 0.3), { weight: 400, ls: 0.3 });
      } else {
        if (num) {
          var nsz = fit(num, Math.min(w * 0.42, h * 0.2), maxW, 'num');
          T(num, w / 2, h * 0.3, nsz);
          L(w / 2 - w * 0.12, h * 0.37, w / 2 + w * 0.12, h * 0.37, sw * 0.8);
        }
        var hsz = w * 0.2;
        lines = wrap2(name, hsz, maxW, 'serif');
        var longest = lines.reduce(function (a, b) { return a.length > b.length ? a : b; });
        hsz = fit(longest, hsz, maxW, 'serif');
        var base = num ? h * 0.53 : h * 0.45;
        for (i = 0; i < lines.length; i++) T(lines[i], w / 2, base + i * hsz * 1.1, hsz, { font: FONT_SERIF, style: 'italic', weight: 400 });
        if (street) T(street, w / 2, h * 0.85, fit(street, w * 0.065, maxW, 'caps', 0.26), { weight: 400, ls: 0.26 });
      }
    }

    else if (model === 'modern') {
      if (!vert) {
        var r = h * 0.32, rcx = m + r, rcy = h / 2;
        C(rcx, rcy, r, sw * 1.1);
        it.push({ t: 'arc', cx: rcx, cy: rcy, r: r + h * 0.06, a0: 200, a1: 330, sw: sw * 0.8 });
        size = fit(num, r * 1.05, r * 1.45, 'num');
        T(num, rcx, rcy + size * 0.36, size, { maxw: r * 1.45 });
        var x0 = rcx + r + w * 0.07, x1 = w - m;
        L(x0, h * 0.3, x1, h * 0.3, sw);
        if (street) {
          var sts = h * 0.085;
          lines = wrap2(street, sts, x1 - x0, 'caps', 0.18);
          var lg = lines.reduce(function (a, b) { return a.length > b.length ? a : b; });
          sts = fit(lg, sts, x1 - x0, 'caps', 0.18);
          var sy = lines.length > 1 ? h * 0.47 : h * 0.54;
          for (i = 0; i < lines.length; i++) T(lines[i], x0, sy + i * sts * 1.45, sts, { weight: 400, ls: 0.18, anchor: 'start', maxw: x1 - x0 });
        }
        L(x0, h * 0.7, x0 + (x1 - x0) * 0.45, h * 0.7, sw);
        L(x0 + (x1 - x0) * 0.52, h * 0.7, x0 + (x1 - x0) * 0.6, h * 0.7, sw);
      } else {
        var rv = w * 0.3, vcx = w / 2, vcy = m + rv + h * 0.04;
        C(vcx, vcy, rv, sw * 1.1);
        it.push({ t: 'arc', cx: vcx, cy: vcy, r: rv + w * 0.06, a0: 20, a1: 160, sw: sw * 0.8 });
        size = fit(num, rv * 1.05, rv * 1.45, 'num');
        T(num, vcx, vcy + size * 0.36, size, { maxw: rv * 1.45 });
        var by = vcy + rv + h * 0.1;
        L(m, by, w - m, by, sw);
        if (street) {
          var vss = w * 0.08;
          lines = wrap2(street, vss, maxW, 'caps', 0.2);
          var lgv = lines.reduce(function (a, b) { return a.length > b.length ? a : b; });
          vss = fit(lgv, vss, maxW, 'caps', 0.2);
          for (i = 0; i < lines.length; i++) T(lines[i], m, by + h * 0.08 + i * vss * 1.5, vss, { weight: 400, ls: 0.2, anchor: 'start' });
        }
        L(m, h - m * 1.1, m + maxW * 0.45, h - m * 1.1, sw);
      }
    }

    else { // custom
      var ins = Math.min(w, h) * 0.06;
      it.push({ t: 'rect', x: ins, y: ins, w: w - 2 * ins, h: h - 2 * ins, sw: sw * 0.7 });
      var c = Math.min(w, h) * 0.07, ci = ins * 1.7;
      [[ci, ci, 1, 1], [w - ci, ci, -1, 1], [ci, h - ci, 1, -1], [w - ci, h - ci, -1, -1]].forEach(function (p) {
        it.push({ t: 'path', d: 'M' + f1(p[0]) + ' ' + f1(p[1] + p[3] * c) + ' L' + f1(p[0]) + ' ' + f1(p[1]) + ' L' + f1(p[0] + p[2] * c) + ' ' + f1(p[1]), sw: sw * 0.8 });
      });
      var nm = house;
      if (!vert) {
        branch(it, w / 2, h * 0.27, w * 0.42, sw);
        if (nm) T(nm, w / 2, h * 0.6, fit(nm, h * 0.22, w - 2 * m, 'serif'), { font: FONT_SERIF, style: 'italic', weight: 400 });
        var bottom = [num, street].filter(Boolean).join('  ·  ');
        if (bottom) T(bottom, w / 2, nm ? h * 0.8 : h * 0.66, fit(bottom, nm ? h * 0.075 : h * 0.16, w - 2 * m, 'caps', 0.26), { weight: 400, ls: 0.26 });
      } else {
        if (num) T(num, w / 2, h * 0.32, fit(num, Math.min(w * 0.46, h * 0.2), maxW, 'num'));
        branch(it, w / 2, h * 0.44, w * 0.55, sw);
        if (nm) {
          var cs = w * 0.17;
          lines = wrap2(nm, cs, maxW, 'serif');
          cs = fit(lines.reduce(function (a, b) { return a.length > b.length ? a : b; }), cs, maxW, 'serif');
          for (i = 0; i < lines.length; i++) T(lines[i], w / 2, h * 0.6 + i * cs * 1.1, cs, { font: FONT_SERIF, style: 'italic', weight: 400 });
        }
        if (street) T(street, w / 2, h * 0.86, fit(street, w * 0.06, maxW, 'caps', 0.24), { weight: 400, ls: 0.24 });
      }
    }
    return it;
  }

  // olive-branch motif (engraved stem + filled leaves)
  function branch(it, cx, cy, span, sw) {
    var P0 = [cx - span / 2, cy + span * 0.04], P1 = [cx, cy - span * 0.07], P2 = [cx + span / 2, cy + span * 0.04];
    it.push({ t: 'path', d: 'M' + f1(P0[0]) + ' ' + f1(P0[1]) + ' Q' + f1(P1[0]) + ' ' + f1(P1[1]) + ' ' + f1(P2[0]) + ' ' + f1(P2[1]), sw: sw });
    function pt(t) {
      var a = (1 - t) * (1 - t), b = 2 * t * (1 - t), c = t * t;
      return [a * P0[0] + b * P1[0] + c * P2[0], a * P0[1] + b * P1[1] + c * P2[1]];
    }
    function tan(t) {
      return Math.atan2(2 * (1 - t) * (P1[1] - P0[1]) + 2 * t * (P2[1] - P1[1]), 2 * (1 - t) * (P1[0] - P0[0]) + 2 * t * (P2[0] - P1[0]));
    }
    var L = span * 0.14, n = 8;
    for (var i = 0; i < n; i++) {
      var t = 0.12 + i * 0.105, p = pt(t), side = i % 2 ? 1 : -1;
      var th = tan(t) + side * 0.72;
      it.push({ t: 'ellipse', cx: p[0] + Math.cos(th) * L * 0.52, cy: p[1] + Math.sin(th) * L * 0.52, rx: L / 2, ry: L * 0.17, rot: th * 180 / Math.PI, fill: true });
    }
    var e = pt(1), te = tan(1);
    it.push({ t: 'ellipse', cx: e[0] + Math.cos(te) * L * 0.45, cy: e[1] + Math.sin(te) * L * 0.45, rx: L / 2, ry: L * 0.17, rot: te * 180 / Math.PI, fill: true });
  }

  function drawItems(items, fill, stroke, extra) {
    return items.map(function (o) {
      switch (o.t) {
        case 'text':
          return '<text x="' + f1(o.x) + '" y="' + f1(o.y) + '" font-family="' + esc(o.font) + '" font-size="' + f1(o.size) +
            '" font-weight="' + o.weight + '" font-style="' + o.style + '" letter-spacing="' + f1(o.ls * o.size) +
            '" text-anchor="' + o.anchor + '" data-maxw="' + f1(o.maxw) + '" fill="' + fill + '"' + (extra || '') + '>' + esc(o.text) + '</text>';
        case 'line':
          return '<line x1="' + f1(o.x1) + '" y1="' + f1(o.y1) + '" x2="' + f1(o.x2) + '" y2="' + f1(o.y2) + '" stroke="' + stroke + '" stroke-width="' + f1(o.sw) + '" stroke-linecap="round"/>';
        case 'circle':
          return '<circle cx="' + f1(o.cx) + '" cy="' + f1(o.cy) + '" r="' + f1(o.r) + '" fill="none" stroke="' + stroke + '" stroke-width="' + f1(o.sw) + '"/>';
        case 'rect':
          return '<rect x="' + f1(o.x) + '" y="' + f1(o.y) + '" width="' + f1(o.w) + '" height="' + f1(o.h) + '" fill="none" stroke="' + stroke + '" stroke-width="' + f1(o.sw) + '"/>';
        case 'path':
          return '<path d="' + o.d + '" fill="none" stroke="' + stroke + '" stroke-width="' + f1(o.sw) + '" stroke-linecap="round" stroke-linejoin="round"/>';
        case 'ellipse':
          return '<ellipse cx="' + f1(o.cx) + '" cy="' + f1(o.cy) + '" rx="' + f1(o.rx) + '" ry="' + f1(o.ry) + '" transform="rotate(' + f1(o.rot) + ' ' + f1(o.cx) + ' ' + f1(o.cy) + ')" fill="' + fill + '"/>';
        case 'arc':
          var a0 = o.a0 * Math.PI / 180, a1 = o.a1 * Math.PI / 180;
          return '<path d="M' + f1(o.cx + o.r * Math.cos(a0)) + ' ' + f1(o.cy + o.r * Math.sin(a0)) + ' A' + f1(o.r) + ' ' + f1(o.r) + ' 0 0 1 ' +
            f1(o.cx + o.r * Math.cos(a1)) + ' ' + f1(o.cy + o.r * Math.sin(a1)) + '" fill="none" stroke="' + stroke + '" stroke-width="' + f1(o.sw) + '" stroke-linecap="round"/>';
      }
      return '';
    }).join('');
  }

  /* ---------- facades (drawn in daylight colours; night darkens) ---------- */
  function facade(type, W, H, u, id) {
    var r = rng(type.length * 977 + 13), s = '', x, y, i;
    if (type === 'render') {
      s += '<rect width="' + W + '" height="' + H + '" fill="#E3DFD7"/>';
      s += '<rect width="' + W + '" height="' + H + '" fill="#000" opacity=".08" filter="url(#' + id + 'grainL)"/>';
    } else if (type === 'cladding') {
      s += '<rect width="' + W + '" height="' + H + '" fill="#141210"/>';
      var bw = 120 * u, gap = 10 * u;
      for (x = -r() * bw; x < W; x += bw + gap) {
        var base = ['#3A2F25', '#352B22', '#3F3328', '#30271F'][Math.floor(r() * 4)];
        s += '<rect x="' + f1(x) + '" y="0" width="' + f1(bw) + '" height="' + H + '" fill="' + base + '"/>';
        for (i = 0; i < 9; i++) {
          var gx = x + r() * bw;
          s += '<line x1="' + f1(gx) + '" y1="0" x2="' + f1(gx + (r() - .5) * 6) + '" y2="' + H + '" stroke="' + (r() > .5 ? '#000' : '#5A4A3B') + '" stroke-opacity="' + f1(.12 + r() * .2) + '" stroke-width="' + f1(.6 + r() * 1.4) + '"/>';
        }
        s += '<rect x="' + f1(x + bw - 3 * u) + '" y="0" width="' + f1(3 * u) + '" height="' + H + '" fill="#000" opacity=".25"/>';
      }
      s += '<rect width="' + W + '" height="' + H + '" fill="#000" opacity=".1" filter="url(#' + id + 'grainL)"/>';
    } else if (type === 'brick') {
      s += '<rect width="' + W + '" height="' + H + '" fill="#9A9184"/>';
      var bH = 65 * u, bW = 215 * u, mo = 10 * u, row = 0;
      for (y = 0; y < H; y += bH + mo, row++) {
        for (x = (row % 2 ? -bW / 2 : 0) - mo; x < W; x += bW + mo) {
          var hue = ['#6E4A38', '#7A5340', '#5E3F31', '#80583F', '#6A4636', '#8A6448'][Math.floor(r() * 6)];
          s += '<rect x="' + f1(x) + '" y="' + f1(y) + '" width="' + f1(bW) + '" height="' + f1(bH) + '" rx="' + f1(1.5 * u) + '" fill="' + hue + '"/>';
          s += '<rect x="' + f1(x) + '" y="' + f1(y + bH - 4 * u) + '" width="' + f1(bW) + '" height="' + f1(4 * u) + '" fill="#000" opacity=".18"/>';
        }
      }
      s += '<rect width="' + W + '" height="' + H + '" fill="#000" opacity=".22" filter="url(#' + id + 'grainH)"/>';
    } else if (type === 'concrete') {
      s += '<rect width="' + W + '" height="' + H + '" fill="#8F8C87"/>';
      var bd = 150 * u;
      for (y = 0; y < H; y += bd) {
        s += '<rect x="0" y="' + f1(y) + '" width="' + W + '" height="' + f1(bd) + '" fill="' + (r() > .5 ? '#fff' : '#000') + '" opacity="' + f1(.02 + r() * .05) + '"/>';
        s += '<line x1="0" y1="' + f1(y) + '" x2="' + W + '" y2="' + f1(y) + '" stroke="#5E5B57" stroke-opacity=".45" stroke-width="' + f1(1.2 * u) + '"/>';
      }
      for (x = 300 * u; x < W; x += 600 * u) for (y = 150 * u; y < H; y += 450 * u) {
        s += '<circle cx="' + f1(x) + '" cy="' + f1(y) + '" r="' + f1(9 * u) + '" fill="#4B4844"/><circle cx="' + f1(x) + '" cy="' + f1(y) + '" r="' + f1(9 * u) + '" fill="none" stroke="#A9A6A0" stroke-width="' + f1(1.2 * u) + '"/>';
      }
      s += '<rect width="' + W + '" height="' + H + '" fill="#000" opacity=".2" filter="url(#' + id + 'grainH)"/>';
    } else { // porcelain — large-format light stone
      s += '<rect width="' + W + '" height="' + H + '" fill="#CFC7B9"/>';
      var pw = 1200 * u, ph = 600 * u;
      for (y = -ph * 0.4; y < H; y += ph) for (x = -pw * 0.55; x < W; x += pw) {
        s += '<rect x="' + f1(x) + '" y="' + f1(y) + '" width="' + f1(pw) + '" height="' + f1(ph) + '" fill="' + (r() > .5 ? '#fff' : '#6b5d48') + '" opacity="' + f1(.03 + r() * .05) + '"/>';
        s += '<rect x="' + f1(x) + '" y="' + f1(y) + '" width="' + f1(pw) + '" height="' + f1(ph) + '" fill="none" stroke="#7C7365" stroke-opacity=".7" stroke-width="' + f1(4 * u) + '"/>';
      }
      s += '<rect width="' + W + '" height="' + H + '" fill="#4a3d2b" opacity=".07" filter="url(#' + id + 'grainL)"/>';
    }
    return s;
  }

  function door(W, H, u, time) {
    var right = W * 0.27, top = H * 0.06, s = '';
    s += '<rect x="-20" y="' + f1(top - 22 * u) + '" width="' + f1(right + 20 + 22 * u) + '" height="' + f1(H) + '" fill="#1B1D1F"/>'; // reveal
    s += '<rect x="-20" y="' + f1(top) + '" width="' + f1(right + 20) + '" height="' + f1(H) + '" fill="#33383C"/>';
    for (var i = 1; i < 4; i++) s += '<line x1="' + f1(right - i * 85 * u - 40 * u) + '" y1="' + f1(top + 20 * u) + '" x2="' + f1(right - i * 85 * u - 40 * u) + '" y2="' + H + '" stroke="#24282B" stroke-width="' + f1(3 * u) + '"/>';
    var hx = right - 70 * u;
    s += '<rect x="' + f1(hx - 7 * u) + '" y="' + f1(H * 0.18) + '" width="' + f1(14 * u) + '" height="' + f1(H * 0.6) + '" rx="' + f1(7 * u) + '" fill="url(#__ID__steel)"/>';
    s += '<rect x="' + f1(right) + '" y="' + f1(top - 22 * u) + '" width="' + f1(24 * u) + '" height="' + H + '" fill="#000" opacity="' + (time === 'night' ? .5 : .28) + '" filter="url(#__ID__soft)"/>';
    return s;
  }

  /* ---------- main render ---------- */
  function render(cfg) {
    var W = cfg.width || 1200, H = cfg.height || 800;
    var id = 'mg' + (++uidCounter) + '_';
    var model = MODELS[cfg.model] ? cfg.model : 'classic';
    var md = MODELS[model];
    var night = cfg.time !== 'day';
    var led = LED[cfg.led] || LED.warm;
    var lighting = cfg.lighting || 'both';
    var haloOn = lighting === 'halo' || lighting === 'both';
    var engOn = lighting === 'engraving' || lighting === 'both';
    var mmW = cfg.wmm || 300, mmH = cfg.hmm || 200;
    if (cfg.orientation === 'v') { var t = mmW; mmW = mmH; mmH = t; }

    var closeup = cfg.view === 'closeup';
    var k = closeup ? Math.min(W * 0.6 / mmW, H * (cfg.fill || 0.62) / mmH)
      : Math.min(PX_PER_MM * (cfg.zoom || 1), H * 0.74 / mmH, W * 0.6 / mmW); // keep large/portrait signs in frame
    var u = closeup ? k * 0.8 : k; // texture scale
    var w = mmW * k, h = mmH * k;
    var cx = cfg.signX || (closeup ? W / 2 : W * 0.62), cy = cfg.signY || (closeup ? H * 0.47 : H * 0.44);
    var sx = cx - w / 2, sy = cy - h / 2;
    var fType = cfg.facade || md.facade;
    var items = layout(model, w, h, cfg);
    var minD = Math.min(w, h);

    var defs = '' +
      '<filter id="' + id + 'grainL" x="0" y="0" width="100%" height="100%"><feTurbulence type="fractalNoise" baseFrequency=".85" numOctaves="2" stitchTiles="stitch"/><feColorMatrix type="saturate" values="0"/><feComponentTransfer><feFuncA type="table" tableValues="0 1"/></feComponentTransfer></filter>' +
      '<filter id="' + id + 'grainH" x="0" y="0" width="100%" height="100%"><feTurbulence type="fractalNoise" baseFrequency=".35" numOctaves="4" stitchTiles="stitch"/><feColorMatrix type="saturate" values="0"/></filter>' +
      '<filter id="' + id + 'soft" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="' + f1(6 * u) + '"/></filter>' +
      '<filter id="' + id + 'shadow" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="' + f1(minD * 0.035 + 3) + '"/></filter>' +
      '<filter id="' + id + 'haloBig" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur stdDeviation="' + f1(minD * 0.16 + 10) + '"/></filter>' +
      '<filter id="' + id + 'haloMid" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="' + f1(minD * 0.05 + 4) + '"/></filter>' +
      '<filter id="' + id + 'haloTight" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="' + f1(minD * 0.012 + 1.5) + '"/></filter>' +
      '<filter id="' + id + 'glow" x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur stdDeviation="' + f1(minD * 0.022 + 1.2) + '" result="a"/><feGaussianBlur in="SourceGraphic" stdDeviation="' + f1(minD * 0.006 + 0.5) + '" result="b"/><feMerge><feMergeNode in="a"/><feMergeNode in="a"/><feMergeNode in="b"/></feMerge></filter>' +
      '<filter id="' + id + 'frost" x="0" y="0" width="100%" height="100%"><feTurbulence type="fractalNoise" baseFrequency="1.6" numOctaves="1" result="n"/><feColorMatrix in="n" type="saturate" values="0" result="g"/><feComposite in="g" in2="SourceGraphic" operator="in" result="t"/><feBlend in="SourceGraphic" in2="t" mode="multiply"/></filter>' +
      '<linearGradient id="' + id + 'steel" x1="0" x2="1"><stop offset="0" stop-color="#7d8287"/><stop offset=".45" stop-color="#e7eaec"/><stop offset="1" stop-color="#5d6266"/></linearGradient>' +
      '<linearGradient id="' + id + 'sun" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#fff8ea" stop-opacity=".16"/><stop offset=".55" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".22"/></linearGradient>' +
      '<radialGradient id="' + id + 'vign" cx=".55" cy=".45" r=".8"><stop offset=".55" stop-color="#000" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity="' + (night ? .6 : .18) + '"/></radialGradient>' +
      '<linearGradient id="' + id + 'glass" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="' + (night ? '#0b0b0d' : '#1d1e22') + '"/><stop offset="1" stop-color="' + (night ? '#040405' : '#08080a') + '"/></linearGradient>' +
      '<linearGradient id="' + id + 'refl" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#fff" stop-opacity="' + (night ? .05 : .16) + '"/><stop offset=".35" stop-color="#fff" stop-opacity="' + (night ? .015 : .05) + '"/><stop offset=".36" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></linearGradient>' +
      '<radialGradient id="' + id + 'lamp" cx=".5" cy="0" r=".5"><stop offset="0" stop-color="#fff" stop-opacity=".9"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></radialGradient>';

    var fac = facade(fType, W, H, u, id) + (closeup ? '' : door(W, H, u, night ? 'night' : 'day').replace(/__ID__/g, id));
    var body = '';
    body += fac;

    if (!night) {
      body += '<rect width="' + W + '" height="' + H + '" fill="url(#' + id + 'sun)"/>';
    } else {
      // darken the whole scene
      body += '<rect width="' + W + '" height="' + H + '" fill="#030406" opacity=".9"/>';
      // soft ambient from a downlight over the door
      if (!closeup) {
        defs += '<mask id="' + id + 'lampMask"><rect width="' + W + '" height="' + H + '" fill="#000"/><ellipse cx="' + f1(W * 0.2) + '" cy="' + f1(H * 0.02) + '" rx="' + f1(W * 0.22) + '" ry="' + f1(H * 0.75) + '" fill="url(#' + id + 'lamp)" opacity=".38"/></mask>';
        body += '<g mask="url(#' + id + 'lampMask)">' + fac + '<rect width="' + W + '" height="' + H + '" fill="#ffcf8a" opacity=".22" style="mix-blend-mode:multiply"/></g>';
      }
      // HALO light reveals the facade texture around the sign
      if (haloOn) {
        var e1 = minD * 0.06;
        defs += '<mask id="' + id + 'haloMask"><rect width="' + W + '" height="' + H + '" fill="#000"/>' +
          '<rect x="' + f1(sx - e1) + '" y="' + f1(sy - e1) + '" width="' + f1(w + 2 * e1) + '" height="' + f1(h + 2 * e1) + '" fill="#fff" filter="url(#' + id + 'haloBig)"/>' +
          '<rect x="' + f1(sx - e1 * 0.3) + '" y="' + f1(sy - e1 * 0.3) + '" width="' + f1(w + e1 * 0.6) + '" height="' + f1(h + e1 * 0.6) + '" fill="#fff" filter="url(#' + id + 'haloMid)"/></mask>';
        body += '<g mask="url(#' + id + 'haloMask)">' + fac +
          '<rect width="' + W + '" height="' + H + '" fill="' + led.halo + '" style="mix-blend-mode:multiply"/>' +
          '<rect width="' + W + '" height="' + H + '" fill="' + led.halo + '" opacity=".28" style="mix-blend-mode:screen"/></g>';
        body += '<rect x="' + f1(sx - 2) + '" y="' + f1(sy - 2) + '" width="' + f1(w + 4) + '" height="' + f1(h + 4) + '" fill="none" stroke="' + led.core + '" stroke-width="' + f1(minD * 0.02 + 2) + '" filter="url(#' + id + 'haloTight)" opacity=".95"/>';
        body += '<rect x="' + f1(sx - e1 * 0.4) + '" y="' + f1(sy - e1 * 0.4) + '" width="' + f1(w + e1 * 0.8) + '" height="' + f1(h + e1 * 0.8) + '" fill="' + led.glow + '" filter="url(#' + id + 'haloMid)" opacity=".55" style="mix-blend-mode:screen"/>';
      }
      body += '<rect width="' + W + '" height="' + H + '" fill="url(#' + id + 'vign)"/>';
    }

    // daylight: cast shadow from the stand-off sign; faint halo if switched on
    var sign = '';
    if (!night) {
      sign += '<rect x="' + f1(sx + minD * 0.03) + '" y="' + f1(sy + minD * 0.06) + '" width="' + f1(w) + '" height="' + f1(h) + '" fill="#000" opacity=".42" filter="url(#' + id + 'shadow)"/>';
      if (haloOn) sign += '<rect x="' + f1(sx - 3) + '" y="' + f1(sy - 3) + '" width="' + f1(w + 6) + '" height="' + f1(h + 6) + '" fill="none" stroke="' + led.glow + '" stroke-width="5" opacity=".18" filter="url(#' + id + 'haloTight)"/>';
    }
    // glass body: edge + face
    var rr = Math.max(1.5, minD * 0.012);
    sign += '<rect x="' + f1(sx + 1.5) + '" y="' + f1(sy + 2.5) + '" width="' + f1(w) + '" height="' + f1(h) + '" rx="' + f1(rr) + '" fill="' + (night ? '#121212' : '#3c3d40') + '"/>';
    sign += '<rect x="' + f1(sx) + '" y="' + f1(sy) + '" width="' + f1(w) + '" height="' + f1(h) + '" rx="' + f1(rr) + '" fill="url(#' + id + 'glass)"/>';

    // engraving
    sign += '<g transform="translate(' + f1(sx) + ' ' + f1(sy) + ')">';
    if (engOn) {
      if (night) {
        sign += '<g opacity=".95" filter="url(#' + id + 'glow)">' + drawItems(items, led.glow, led.glow) + '</g>';
        sign += '<g>' + drawItems(items, led.core, led.core) + '</g>';
      } else {
        sign += '<g opacity=".55" filter="url(#' + id + 'glow)">' + drawItems(items, led.glow, led.glow) + '</g>';
        sign += '<g>' + drawItems(items, '#FBF7EE', '#FBF7EE') + '</g>';
      }
    } else {
      // unlit engraving: frosted, reads as satin grey against the black paint
      var unlit = night ? (haloOn ? '#57534c' : '#2a2926') : '#C9C3B7';
      sign += '<g opacity="' + (night ? .8 : .78) + '" filter="url(#' + id + 'frost)">' + drawItems(items, unlit, unlit) + '</g>';
    }
    sign += '</g>';
    // surface reflections + polished edge highlight
    sign += '<rect x="' + f1(sx) + '" y="' + f1(sy) + '" width="' + f1(w) + '" height="' + f1(h) + '" rx="' + f1(rr) + '" fill="url(#' + id + 'refl)"/>';
    sign += '<rect x="' + f1(sx + .5) + '" y="' + f1(sy + .5) + '" width="' + f1(w - 1) + '" height="' + f1(h - 1) + '" rx="' + f1(rr) + '" fill="none" stroke="' + (night && haloOn ? led.glow : '#ffffff') + '" stroke-opacity="' + (night ? (haloOn ? .35 : .08) : .28) + '" stroke-width="1"/>';

    // dimension annotation
    var dims = '';
    if (cfg.dims) {
      var dc = night ? 'rgba(242,233,220,.75)' : 'rgba(20,18,15,.75)';
      var dy = sy + h + 26 + minD * 0.05, dx = sx + w + 26 + minD * 0.05, tk = 7;
      dims += '<g stroke="' + dc + '" stroke-width="1" fill="none">' +
        '<line x1="' + f1(sx) + '" y1="' + f1(dy) + '" x2="' + f1(sx + w) + '" y2="' + f1(dy) + '"/>' +
        '<line x1="' + f1(sx) + '" y1="' + f1(dy - tk) + '" x2="' + f1(sx) + '" y2="' + f1(dy + tk) + '"/>' +
        '<line x1="' + f1(sx + w) + '" y1="' + f1(dy - tk) + '" x2="' + f1(sx + w) + '" y2="' + f1(dy + tk) + '"/>' +
        '<line x1="' + f1(dx) + '" y1="' + f1(sy) + '" x2="' + f1(dx) + '" y2="' + f1(sy + h) + '"/>' +
        '<line x1="' + f1(dx - tk) + '" y1="' + f1(sy) + '" x2="' + f1(dx + tk) + '" y2="' + f1(sy) + '"/>' +
        '<line x1="' + f1(dx - tk) + '" y1="' + f1(sy + h) + '" x2="' + f1(dx + tk) + '" y2="' + f1(sy + h) + '"/></g>' +
        '<g fill="' + dc + '" font-family="Inter, Arial, sans-serif" font-size="' + (closeup ? 15 : 13) + '" letter-spacing="1">' +
        '<text x="' + f1(sx + w / 2) + '" y="' + f1(dy + 22) + '" text-anchor="middle">' + mmW + ' mm</text>' +
        '<text transform="translate(' + f1(dx + 20) + ' ' + f1(sy + h / 2) + ') rotate(90)" text-anchor="middle">' + mmH + ' mm</text></g>';
    }

    var label = cfg.label === false ? '' :
      '<text x="' + (W - 18) + '" y="28" text-anchor="end" font-family="Inter, Arial, sans-serif" font-size="12" letter-spacing="1.5" fill="' + (night ? 'rgba(242,233,220,.55)' : 'rgba(255,255,255,.8)') + '" style="text-transform:uppercase">CONCEPT VISUALISATION · NOT A PHOTOGRAPH</text>';

    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + W + ' ' + H + '" preserveAspectRatio="xMidYMid slice" role="img" aria-label="' +
      esc(md.name + ' illuminated black glass house sign, ' + (night ? 'night' : 'day') + ' concept visualisation') + '"><defs>' + defs + '</defs>' + body + sign + dims + label + '</svg>';
  }

  /* exploded construction diagram */
  function construction(night) {
    var W = 1200, H = 800, s = '';
    var bg = '#100F0D', ink = 'rgba(242,233,220,.86)', mute = 'rgba(242,233,220,.5)';
    var layers = [
      { n: 'Facade / wall', d: 'Fixings and cable entry by installer', c: '#3b3833', o: .9 },
      { n: 'Concealed mounting frame', d: 'Holds the sign off the wall; hidden from view', c: '#5e5a53', o: .95 },
      { n: 'LED module + enclosed IP67 driver', d: '230 V supply · 3000K or 6000K', c: '#C8A46A', o: .95 },
      { n: 'Black Lacobel glass', d: 'Reverse-engraved from the rear', c: '#0a0a0b', o: 1 }
    ];
    var pw = 360, ph = 250, dx = 105, dyy = -75, x0 = 60, y0 = 640;
    layers.forEach(function (L, i) {
      var x = x0 + i * dx, y = y0 + i * dyy, sk = 70;
      var poly = [[x, y], [x + pw, y - sk * 0.2], [x + pw, y - ph - sk * 0.2], [x, y - ph]].map(function (p) { return p.join(','); }).join(' ');
      if (i === 2) {
        s += '<polygon points="' + poly + '" fill="#1d1b18" stroke="' + L.c + '" stroke-width="2"/>';
        for (var j = 0; j < 9; j++) { var lx = x + 30 + j * 44; s += '<circle cx="' + lx + '" cy="' + (y - 18 - j * 0.9) + '" r="4" fill="#FFE2B0"/><circle cx="' + lx + '" cy="' + (y - ph + 18 - j * 0.9) + '" r="4" fill="#FFE2B0"/>'; }
        s += '<rect x="' + (x + pw / 2 - 60) + '" y="' + (y - ph / 2 - 30) + '" width="120" height="50" rx="4" fill="#2b2925" stroke="#C8A46A"/><text x="' + (x + pw / 2) + '" y="' + (y - ph / 2) + '" fill="#C8A46A" font-family="Inter,Arial" font-size="14" text-anchor="middle">IP67 driver</text>';
      } else if (i === 3) {
        s += '<polygon points="' + poly + '" fill="#09090a" stroke="rgba(255,255,255,.35)" stroke-width="1.5"/>';
        s += '<text x="' + (x + pw / 2) + '" y="' + (y - ph / 2 + 30) + '" fill="#FFF1D6" font-family="Jost,Arial" font-weight="300" font-size="120" text-anchor="middle" filter="url(#cglow)">27</text>';
      } else {
        s += '<polygon points="' + poly + '" fill="' + L.c + '" opacity="' + L.o + '" stroke="rgba(255,255,255,.12)"/>';
        if (i === 1) s += '<rect x="' + (x + 40) + '" y="' + (y - ph + 40) + '" width="' + (pw - 80) + '" height="' + (ph - 80) + '" fill="none" stroke="#2a2824" stroke-width="10"/>';
      }
      var ty = 220 + (3 - i) * 95;
      s += '<line x1="' + (x + pw) + '" y1="' + (y - ph * 0.6) + '" x2="' + 790 + '" y2="' + ty + '" stroke="' + mute + '" stroke-dasharray="3 4"/>';
      s += '<circle cx="790" cy="' + ty + '" r="3" fill="' + ink + '"/>';
      s += '<text x="804" y="' + (ty - 2) + '" fill="' + ink + '" font-family="Jost,Arial" font-size="17" letter-spacing="1.5">' + esc(L.n.toUpperCase()) + '</text>';
      s += '<text x="804" y="' + (ty + 18) + '" fill="' + mute + '" font-family="Inter,Arial" font-size="12.5">' + esc(L.d) + '</text>';
    });
    s += '<text x="40" y="60" fill="' + ink + '" font-family="Jost,Arial" font-size="22" letter-spacing="4" font-weight="300">INDICATIVE CONSTRUCTION</text>';
    s += '<text x="40" y="86" fill="' + mute + '" font-family="Inter,Arial" font-size="13">Exploded view for illustration — final build subject to production confirmation.</text>';
    s += '<text x="40" y="' + (H - 30) + '" fill="' + mute + '" font-family="Inter,Arial" font-size="12.5">Connection to the electrical installation and protection of the supply are the responsibility of a qualified installer.</text>';
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + W + ' ' + H + '" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Indicative construction diagram"><defs><filter id="cglow" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="6" result="a"/><feMerge><feMergeNode in="a"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs><rect width="' + W + '" height="' + H + '" fill="' + bg + '"/>' + s + '</svg>';
  }

  /* After insertion into the DOM: shrink any text wider than its box */
  function fitTexts(root) {
    var nodes = root.querySelectorAll('text[data-maxw]');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i], max = parseFloat(n.getAttribute('data-maxw'));
      var len = n.getComputedTextLength ? n.getComputedTextLength() : 0;
      if (len > max && len > 0) {
        var fs = parseFloat(n.getAttribute('font-size')) * max / len * 0.98;
        n.setAttribute('font-size', f1(fs));
        var ls = parseFloat(n.getAttribute('letter-spacing')) || 0;
        n.setAttribute('letter-spacing', f1(ls * max / len));
      }
    }
  }

  global.MGGSign = {
    render: render, construction: construction, fitTexts: fitTexts,
    MODELS: MODELS, SIZES: SIZES, LED: LED, LIGHTING: LIGHTING
  };
})(typeof window !== 'undefined' ? window : globalThis);
